import type { GigStatus } from '@stage/contracts';

/**
 * Gig lifecycle gating (§13.1, §13.3). The API is authoritative and re-checks every transition with
 * optimistic concurrency; these predicates only decide which controls are enabled in the UI.
 *
 * ```text
 * draft → published → live ↔ intermission → ended → archived
 * ```
 */
export const lifecycle = {
  canPublish: (s: GigStatus): boolean => s === 'draft',
  canStart: (s: GigStatus): boolean => s === 'published',
  canIntermission: (s: GigStatus): boolean => s === 'live',
  canResume: (s: GigStatus): boolean => s === 'intermission',
  canEnd: (s: GigStatus): boolean => s === 'live' || s === 'intermission',
  /** True while the gig is running and the audience page polls the live snapshot (§13.2). */
  isRunning: (s: GigStatus): boolean => s === 'live' || s === 'intermission',
};

/** Human-facing label for a gig status. */
export function statusLabel(s: GigStatus): string {
  switch (s) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'live':
      return 'Live';
    case 'intermission':
      return 'Intermission';
    case 'ended':
      return 'Ended';
    case 'archived':
      return 'Archived';
    case 'cancelled':
      return 'Cancelled';
    case 'suspended':
      return 'Suspended';
    default:
      return s;
  }
}
